import React, { useState, useEffect, Suspense, lazy } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Container, AppBar, Toolbar, Typography, Avatar, IconButton, Snackbar, Alert, Drawer, Badge, Button, Tooltip } from '@mui/material';
import { DataObject, Logout, Queue, ListAlt, Home, ChatBubbleOutline, MenuBook, Description, ChevronRight, Check, SmartToy, Refresh } from '@mui/icons-material';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import monochromeTheme from './theme/monochromeTheme';
import LoginScreen from './components/auth/LoginScreen';
import UploadTTLTab from './UploadTTLTab';
import RepositoryTab from './RepositoryTab';
import ProjectsTab from './ProjectsTab';
import DocumentsTab from './DocumentsTab';
import MasterRequirementsManager from './MasterRequirementsManager';
import TranslationConfigManager from './TranslationConfigManager';
import GlossaryManager from './GlossaryManager';
import SemanticExplorerTab from './semanticExplorer/SemanticExplorerTab';
import ProcessingPanel from './components/processing/ProcessingPanel';
import LayoutHeader from './components/Layout/Header';
import LayoutFooter from './components/Layout/Footer';
import SmartResultsDialog from './components/dialogs/SmartResultsDialog';
import TTLDownloadDialog from './components/dialogs/TTLDownloadDialog';
import DeleteConfirmDialog from './components/dialogs/DeleteConfirmDialog';
import AnalysisDialog from './components/dialogs/AnalysisDialog';
import useSnackbar from './hooks/useSnackbar';
import useProcessingMonitor from './hooks/useProcessingMonitor';
import useSmartAnalysis from './hooks/useSmartAnalysis';
import { loginUser, logoutUser } from './api/authApi';
import { loadFiles as fetchFiles, deleteFile, downloadExcel } from './api/fileApi';
import { loadTranslationConfigs as fetchConfigs } from './api/translationApi';
import { getUserPermissions } from './api/permissionsApi';
import { analysisApi } from './api/analysisApi';
import { setAuthToken } from './api/axios';
import axios from './api/axios';
import './App.css';
import GlobalChatLauncher from './features/ai-workspace/components/overlay/GlobalChatLauncher';
import { GlobalRestartProvider, useGlobalRestart } from './context/GlobalRestartContext';

const AIWorkspacePage = lazy(() => import('./features/ai-workspace/pages/AIWorkspacePage'));

function RestartButton() {
  return (
    <Tooltip title="Start new conversation">
      <IconButton 
        color="inherit" 
        sx={{ mr: 2 }}
        onClick={() => window.location.reload()}
      >
        <Refresh />
      </IconButton>
    </Tooltip>
  );
}

function ProjectWorkspacePage({ showSuccess, showError, userPermissions, onOpenProject, filesCount, processingQueueCount, onToggleProcessingPanel, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const normalizedProjectId = Number(projectId || 0);

  return (
    <ProjectsTab
      showSuccess={showSuccess}
      showError={showError}
      userPermissions={userPermissions}
      onOpenProject={onOpenProject}
      initialProjectId={normalizedProjectId}
      projectDialogFullScreen
      filesCount={filesCount}
      processingQueueCount={processingQueueCount}
      onToggleProcessingPanel={onToggleProcessingPanel}
      onLogout={onLogout}
      onNavigateHome={() => navigate('/')}
      onCloseProjectModal={() => navigate('/projects')}
    />
  );
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Files and configs
  const [files, setFiles] = useState([]);
  const [translationConfigs, setTranslationConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);
  const [fileProgress, setFileProgress] = useState({});
  const [smartProgress, setSmartProgress] = useState({});
  const [userPermissions, setUserPermissions] = useState(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [showConfigManager, setShowConfigManager] = useState(false);

  // Dialogs
  const [analysisDialog, setAnalysisDialog] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [cancellingQueueItemId, setCancellingQueueItemId] = useState(null);
  const [resumingQueueItemId, setResumingQueueItemId] = useState(null);
  const [confirmTTLDownloadOpen, setConfirmTTLDownloadOpen] = useState(false);
  const [selectedFileForDownload, setSelectedFileForDownload] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedFileForDelete, setSelectedFileForDelete] = useState(null);
  const [confirmDeleteAllOpen, setConfirmDeleteAllOpen] = useState(false);

  // Custom hooks
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();
  const { 
    processingQueue, 
    currentProcessing, 
    processingPanelOpen, 
    setProcessingPanelOpen,
    fetchProcessingQueue,
    queueUnavailable,
    lastQueueRefresh,
  } = useProcessingMonitor(isLoggedIn);

  const handleToggleProcessingPanel = (forceOpen = null) => {
    if (typeof forceOpen === 'boolean') {
      setProcessingPanelOpen(forceOpen);
      return;
    }
    setProcessingPanelOpen(!processingPanelOpen);
  };
  const { smartResults, showSmartDialog, setShowSmartDialog, handleStartSmartAnalysis: startSmartAnalysis, handleViewSmartResults } = useSmartAnalysis();

  // Initialize auth token on app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    if (isLoggedIn) {
      loadFiles();
      loadTranslationConfigs();
      loadUserPermissions();
    }
  }, [isLoggedIn]);

  const loadUserPermissions = async () => {
    try {
      const permissions = await getUserPermissions();
      setUserPermissions(permissions);
      const resolvedUserName = permissions?.username || permissions?.user?.username;
      if (resolvedUserName) {
        localStorage.setItem('username', resolvedUserName);
      }
    } catch (error) {
      console.error('[App] Error loading user permissions:', error);
    }
  };

  // Auto-select default config
  useEffect(() => {
    if (translationConfigs.length > 0 && !selectedConfigId) {
      const defaultConfig = translationConfigs.find(c => c.is_default) || translationConfigs[0];
      if (defaultConfig) setSelectedConfigId(defaultConfig.id);
    }
  }, [translationConfigs, selectedConfigId]);

  // Poll progress for all files
  useEffect(() => {
    if (!isLoggedIn || files.length === 0) return;

    const fetchAllProgress = async () => {
      try {
        // Single bulk request replaces N*2 individual requests
        const response = await axios.get('files/bulk-progress/');
        const bulk = response.data; // { [file_id]: { translation: {...}, smart: {...} } }

        const newFileProgress = {};
        const newSmartProgress = {};

        for (const [fileId, data] of Object.entries(bulk)) {
          newFileProgress[Number(fileId)] = data.translation;
          newSmartProgress[Number(fileId)] = data.smart;
        }

        setFileProgress(newFileProgress);
        setSmartProgress(newSmartProgress);
      } catch (error) {
        console.error('Error fetching bulk progress:', error);

        // Fallback: parallel individual requests if bulk endpoint is unavailable
        if (error.response?.status === 404) {
          const newFileProgress = {};
          const newSmartProgress = {};
          let filesNeedRefresh = false;

          await Promise.all(files.map(async (file) => {
            const [translationResult, smartResult] = await Promise.allSettled([
              axios.get(`files/${file.id}/translation-progress/`),
              axios.get(`files/${file.id}/smart-analysis/progress/`)
            ]);

            if (translationResult.status === 'fulfilled') {
              newFileProgress[file.id] = translationResult.value.data;
            } else if (translationResult.reason?.response?.status === 404) {
              filesNeedRefresh = true;
            }

            if (smartResult.status === 'fulfilled') {
              newSmartProgress[file.id] = smartResult.value.data;
            } else if (smartResult.reason?.response?.status === 404) {
              filesNeedRefresh = true;
            }
          }));

          setFileProgress(newFileProgress);
          setSmartProgress(newSmartProgress);

          if (filesNeedRefresh) {
            try {
              const data = await fetchFiles();
              setFiles(data.files || data);
            } catch (err) {
              console.error('Error refreshing file list:', err);
            }
          }
        }
      }
    };

    // Initial fetch
    fetchAllProgress();

    // Poll every 3 seconds
    const interval = setInterval(fetchAllProgress, 3000);

    return () => clearInterval(interval);
  }, [isLoggedIn, files]);

  // API functions
  const loadFiles = async () => {
    try {
      setLoading(true);
      const data = await fetchFiles();
      setFiles(data.files || data); // Handle both { files: [...] } and [...] formats
    } catch (error) {
      showError('Error loading files');
    } finally {
      setLoading(false);
    }
  };

  const loadTranslationConfigs = async () => {
    try {
      const data = await fetchConfigs();
      setTranslationConfigs(data);
    } catch (error) {
      console.error('Error loading configs:', error);
      showError('Error al cargar las configuraciones de traducción');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginUser(username, password);
      localStorage.setItem('username', username);
      setIsLoggedIn(true);
      showSuccess('Login successful!');
      navigate('/', { replace: true });
      loadFiles();
      loadTranslationConfigs();
      loadUserPermissions();
    } catch (error) {
      showError('Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logoutUser();
    localStorage.removeItem('username');
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
    setFiles([]);
    setUserPermissions(null);
  };

  const handleDeleteFile = async (fileId) => {
    // Check delete permission
    if (!userPermissions?.can_delete) {
      showError('You do not have permission to delete files. Only Admin users can delete.');
      return;
    }
    
    const file = files.find(f => f.id === fileId);
    if (file) {
      setSelectedFileForDelete(file);
      setConfirmDeleteOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedFileForDelete) return;
    const deletedId = selectedFileForDelete.id;
    try {
      await deleteFile(deletedId);
      showSuccess('File deleted successfully!');
      setConfirmDeleteOpen(false);
      setSelectedFileForDelete(null);
      // Optimistically remove from state immediately so the polling loop
      // stops querying the deleted file before loadFiles() completes.
      setFiles(prev => prev.filter(f => f.id !== deletedId));
      setFileProgress(prev => { const next = { ...prev }; delete next[deletedId]; return next; });
      setSmartProgress(prev => { const next = { ...prev }; delete next[deletedId]; return next; });
      loadFiles();
    } catch (error) {
      showError('Delete failed.');
    }
  };

  const handleRequestDeleteAll = () => {
    if (!userPermissions?.can_delete) {
      showError('You do not have permission to delete files. Only Admin users can delete.');
      return;
    }
    setConfirmDeleteAllOpen(true);
  };

  const handleConfirmDeleteAll = async () => {
    const allIds = files.map(f => f.id);
    try {
      await Promise.all(allIds.map(id => deleteFile(id)));
      setConfirmDeleteAllOpen(false);
      setFiles([]);
      setFileProgress({});
      setSmartProgress({});
      showSuccess(`${allIds.length} file(s) deleted successfully.`);
    } catch (error) {
      showError('Error deleting some files.');
      setConfirmDeleteAllOpen(false);
      loadFiles();
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const stats = await downloadExcel(file.id);
      setSelectedFileForDownload({ ...file, ...stats });
      setConfirmTTLDownloadOpen(true);
    } catch (error) {
      showError('Error loading file for download');
    }
  };

  const handleConfirmTTLDownload = async () => {
    if (!selectedFileForDownload) return;
    try {
      await downloadExcel(selectedFileForDownload.id, true);
      showSuccess('Excel file downloaded successfully');
      setConfirmTTLDownloadOpen(false);
      setSelectedFileForDownload(null);
    } catch (error) {
      showError('Error downloading Excel file');
    }
  };

  const handleViewAnalysis = async (fileId) => {
    try {
      setAnalysisDialog(true);
      setSelectedAnalysis({ loading: true }); // Show loading state
      
      const data = await analysisApi.getFileAnalysis(fileId, {
        page: 1,
        page_size: 50
      });
      
      setSelectedAnalysis(data);
    } catch (error) {
      console.error('[handleViewAnalysis] Error fetching analysis:', error);
      showError(error.response?.data?.error || 'Error loading analysis');
      setAnalysisDialog(false);
      setSelectedAnalysis(null);
    }
  };

  const handleStartSmartAnalysis = async (fileId) => {
    try {
      await startSmartAnalysis(fileId);
      showSuccess('SMART analysis started successfully!');
    } catch (error) {
      console.error('[handleStartSmartAnalysis] Error starting SMART analysis:', error);
      const errorMessage = error.message 
        || error.response?.data?.error 
        || error.response?.data?.message 
        || 'Error starting SMART analysis';
      showError(errorMessage);
    }
  };

  const handleOpenProject = (project) => {
    if (project?.name) {
      showSuccess(`Opening project: ${project.name}`);
    }
    if (project?.id) {
      navigate(`/projects/${project.id}`, { state: { projectName: project.name || '' } });
      return;
    }
    navigate('/projects');
  };

  const handleCancelQueueItem = async (item) => {
    const normalizedItemId = String(item?.id ?? '').trim();
    let normalizedType = String(item?.type ?? '').trim().toLowerCase();

    if (normalizedItemId.startsWith('ttl_translate_')) {
      normalizedType = 'ttl_translation';
    }

    if (!normalizedItemId || !normalizedType) {
      showError('Invalid queue task');
      return;
    }

    try {
      setCancellingQueueItemId(item.id);
      const result = await analysisApi.cancelProcessingTask(normalizedItemId, normalizedType);
      showSuccess(result?.message || 'Task cancelled');
      await fetchProcessingQueue();
      await loadFiles();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error cancelling task';
      showError(errorMessage);
    } finally {
      setCancellingQueueItemId(null);
    }
  };

  const handleResumeQueueItem = async (item) => {
    const normalizedItemId = String(item?.id ?? '').trim();
    const normalizedType = String(item?.type ?? '').trim().toLowerCase();

    if (!normalizedItemId || !normalizedType) {
      showError('Invalid queue task');
      return;
    }

    try {
      setResumingQueueItemId(item.id);
      await analysisApi.resumeProcessingTask(normalizedItemId, normalizedType);
      showSuccess('Task resumed from saved progress');
      await fetchProcessingQueue();
      await loadFiles();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error resuming task';
      showError(errorMessage);
    } finally {
      setResumingQueueItemId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const homeCards = [
    {
      key: 'projects',
      title: 'Projects',
      description: 'Manage initiatives, organize deliverables, and open the full workflow for each project.',
      features: [
        'Centralized project listing',
        'Direct access to the workspace',
        'Status and activity tracking',
        'Integrated ontology navigation'
      ],
      buttonLabel: 'Go to Projects',
      icon: <ListAlt fontSize="large" />,
      path: '/projects',
      enabled: true
    },
    {
      key: 'workspace',
      title: 'AI Workspace',
      description: 'Open the full engineering workspace with synchronized chat, evidence, document, graph, and HITL operations.',
      features: [
        'Unified multimodal investigation flow',
        'Persistent evidence and confidence panel',
        'Human-in-the-loop decision queue',
        'Streaming agent state and tool traceability'
      ],
      buttonLabel: 'Open Workspace',
      icon: <SmartToy fontSize="large" />,
      path: '/workspace',
      enabled: true
    },
    {
      key: 'glossaries',
      title: 'Glossaries',
      description: 'Control key terms, definitions, and consistency to maintain cross-functional quality.',
      features: [
        'Terminology governance',
        'Definition versioning',
        'Translation synchronization',
        'Semantic consistency control'
      ],
      buttonLabel: 'Go to Glossaries',
      icon: <MenuBook fontSize="large" />,
      path: '/glossaries',
      enabled: true
    },
    {
      key: 'documents',
      title: 'Documents',
      description: 'Upload and manage standalone documents quickly for analysis, review, and downstream processing.',
      features: [
        'Individual document upload',
        'File status and processing tracking',
        'Direct review of uploaded content',
        'Ready for later project integration'
      ],
      buttonLabel: 'Go to Documents',
      icon: <Description fontSize="large" />,
      path: '/documents',
      enabled: true
    }
  ];

  const loggedUserName = userPermissions?.username
    || userPermissions?.user?.username
    || localStorage.getItem('username')
    || username
    || 'User';
  const queueCount = processingQueue.length + (currentProcessing ? 1 : 0);

  const isHomeRoute = location.pathname === '/';
  const isProjectDetailRoute = /^\/projects\/\d+/.test(location.pathname);
  const isWorkspaceRoute = location.pathname.startsWith('/workspace');

  if (!isLoggedIn) {
    return (
      <ThemeProvider theme={monochromeTheme}>
        <LoginScreen
          username={username}
          password={password}
          loading={loading}
          setUsername={setUsername}
          setPassword={setPassword}
          handleLogin={handleLogin}
          snackbar={snackbar}
          hideSnackbar={hideSnackbar}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={monochromeTheme}>
      <Box sx={{ minHeight: '100dvh', height: '100dvh', background: '#f3f4f6', display: 'flex', flexDirection: 'column' }}>
        {isHomeRoute ? (
          <LayoutHeader
            userName={loggedUserName}
            onLogout={handleLogout}
            extraActions={(
              <IconButton aria-label="Open processing panel" onClick={() => handleToggleProcessingPanel(true)} color="inherit" sx={{ mr: 1.5 }}>
                <Badge badgeContent={queueCount} color="primary">
                  <Queue />
                </Badge>
              </IconButton>
            )}
          />
        ) : (
          <AppBar
            position="static"
            elevation={0}
            sx={{
              background: '#000000',
              borderBottom: isWorkspaceRoute ? 'none' : '1px solid #212121',
            }}
          >
            <Toolbar>
              <Avatar sx={{ bgcolor: '#ffffff', color: '#000000', mr: 2 }}>
                <DataObject />
              </Avatar>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#ffffff', lineHeight: 1.1 }}>
                  Requirements Platform
                </Typography>
                {isWorkspaceRoute && (
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)', letterSpacing: 0.4 }}>
                    AI Workspace
                  </Typography>
                )}
              </Box>
              <Button
                startIcon={<Home />}
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{
                  mr: 2,
                  color: '#ffffff',
                  borderColor: 'rgba(255,255,255,0.35)',
                  '&:hover': {
                    borderColor: '#ffffff',
                    backgroundColor: 'rgba(255,255,255,0.08)'
                  }
                }}
              >
                Home
              </Button>
              {isProjectDetailRoute && (
                <Button
                  variant="outlined"
                  onClick={() => navigate('/projects')}
                  sx={{
                    mr: 2,
                    color: '#ffffff',
                    borderColor: 'rgba(255,255,255,0.35)',
                    '&:hover': {
                      borderColor: '#ffffff',
                      backgroundColor: 'rgba(255,255,255,0.08)'
                    }
                  }}
                >
                  Back to Projects
                </Button>
              )}
              {isWorkspaceRoute && (
                <RestartButton />
              )}
              <IconButton aria-label="Open processing panel" onClick={() => handleToggleProcessingPanel()} color="inherit" sx={{ mr: 2 }}>
                <Badge badgeContent={processingQueue.length + (currentProcessing ? 1 : 0)} color="primary">
                  <Queue />
                </Badge>
              </IconButton>
              <IconButton color="inherit" onClick={handleLogout}>
                <Logout />
              </IconButton>
            </Toolbar>
          </AppBar>
        )}

        <Container
          maxWidth={false}
          disableGutters={isWorkspaceRoute}
          sx={isWorkspaceRoute
            ? {
                p: 0,
                px: 0,
                width: '100%',
                flex: 1,
                minHeight: 0,
              }
            : isHomeRoute
              ? {
                  p: 0,
                  m: 0,
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                }
              : {
                  flex: 1,
                  minHeight: 0,
                  py: { xs: 2, sm: 2.5, md: 3, lg: 3.5 },
                  px: { xs: 1.5, sm: 2, md: 2.25, lg: 3 },
                  pr: processingPanelOpen
                    ? { xs: 1.5, sm: 2, md: 2.25, lg: 'min(400px, 28vw)' }
                    : { xs: 1.5, sm: 2, md: 2.25, lg: 3 },
                  transition: 'padding-right 0.3s, padding 0.3s',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                }}
        >
          <Suspense fallback={<Box sx={{ py: 6, textAlign: 'center' }}><Typography>Loading workspace...</Typography></Box>}>
          <Routes>
            <Route
              path="/"
              element={
                <Box className="home-page">
                  <Box className="home-shell">
                    <Box className="home-hero">
                      <Typography variant="h3" className="home-title">
                        Requirements Platform
                      </Typography>
                      <Typography className="home-subtitle">
                        Select the module you want to use
                      </Typography>
                    </Box>

                    <Box className="home-grid">
                      {homeCards.map((card) => (
                        <div
                          key={card.key}
                          className={`home-card ${!card.enabled ? 'home-card-disabled' : ''}`}
                          onClick={() => card.enabled && navigate(card.path)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if ((event.key === 'Enter' || event.key === ' ') && card.enabled) {
                              event.preventDefault();
                              navigate(card.path);
                            }
                          }}
                        >
                          <Box className="home-card-header">
                            <Box className="home-icon-wrap">
                              <Box className="home-card-icon">{card.icon}</Box>
                              <Typography className="home-card-title">{card.title}</Typography>
                            </Box>
                          </Box>

                          <Box className="home-card-body">
                            <Typography className="home-card-description">{card.description}</Typography>

                            <Box component="ul" className="home-features">
                              {card.features.map((feature) => (
                                <Box component="li" key={feature}>
                                  <span className="home-feature-check">
                                    <Check sx={{ fontSize: 11 }} />
                                  </span>
                                  {feature}
                                </Box>
                              ))}
                            </Box>

                            <button type="button" className="home-card-btn" disabled={!card.enabled}>
                              <ChevronRight sx={{ fontSize: 15 }} />
                              {card.buttonLabel}
                            </button>
                          </Box>
                        </div>
                      ))}
                    </Box>
                  </Box>

                </Box>
              }
            />
            <Route path="/upload" element={<UploadTTLTab selectedConfigId={selectedConfigId} translationConfigs={translationConfigs} setSelectedConfigId={setSelectedConfigId} setShowConfigManager={setShowConfigManager} onUploadSuccess={() => { loadFiles(); navigate('/repository'); }} showSuccess={showSuccess} showError={showError} userPermissions={userPermissions} />} />
            <Route path="/repository" element={<RepositoryTab files={files} loading={loading} fileProgress={fileProgress} smartProgress={smartProgress} handleDownloadFile={handleDownloadFile} handleViewAnalysis={handleViewAnalysis} handleStartSmartAnalysis={handleStartSmartAnalysis} handleDeleteFile={handleDeleteFile} handleDeleteAllFiles={handleRequestDeleteAll} loadFiles={loadFiles} formatFileSize={formatFileSize} userPermissions={userPermissions} />} />
            <Route path="/projects" element={<ProjectsTab showSuccess={showSuccess} showError={showError} userPermissions={userPermissions} onOpenProject={handleOpenProject} />} />
            <Route path="/projects/:projectId" element={<ProjectWorkspacePage showSuccess={showSuccess} showError={showError} userPermissions={userPermissions} onOpenProject={handleOpenProject} filesCount={files.length} processingQueueCount={processingQueue.length + (currentProcessing ? 1 : 0)} onToggleProcessingPanel={handleToggleProcessingPanel} onLogout={handleLogout} />} />
            <Route path="/documents" element={<DocumentsTab showSuccess={showSuccess} showError={showError} />} />
            <Route path="/master-requirements" element={<MasterRequirementsManager setCurrentTab={() => {}} setProcessingPanelOpen={setProcessingPanelOpen} currentProcessing={currentProcessing} userPermissions={userPermissions} />} />
            <Route path="/glossaries" element={<GlossaryManager embedded userPermissions={userPermissions} />} />
            <Route path="/chat" element={<SemanticExplorerTab showSuccess={showSuccess} showError={showError} />} />
            <Route path="/agent" element={<Navigate to="/workspace" replace />} />
            <Route path="/workspace" element={<AIWorkspacePage />} />
            <Route path="/workspace/:projectId" element={<AIWorkspacePage />} />
            <Route path="/ontologies" element={<DocumentsTab showSuccess={showSuccess} showError={showError} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </Container>

        <LayoutFooter />

        {isLoggedIn && <GlobalChatLauncher hidden={processingPanelOpen} />}

        <Drawer
          variant="persistent"
          anchor="right"
          open={processingPanelOpen}
          sx={{
            width: { xs: '100vw', sm: 340, md: 360, lg: 400 },
            zIndex: (theme) => theme.zIndex.modal + 2,
            '& .MuiDrawer-paper': {
              width: { xs: '100vw', sm: 340, md: 360, lg: 400 },
              bgcolor: '#fafafa',
              zIndex: (theme) => theme.zIndex.modal + 2,
            },
          }}
        >
          <ProcessingPanel
            currentProcessing={currentProcessing}
            processingQueue={processingQueue}
            onClose={() => setProcessingPanelOpen(false)}
            onCancelQueueItem={handleCancelQueueItem}
            onResumeQueueItem={handleResumeQueueItem}
            cancellingQueueItemId={cancellingQueueItemId}
            resumingQueueItemId={resumingQueueItemId}
            onRefresh={fetchProcessingQueue}
            queueUnavailable={queueUnavailable}
            lastQueueRefresh={lastQueueRefresh}
          />
        </Drawer>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={hideSnackbar}>
          <Alert onClose={hideSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>

        <TTLDownloadDialog open={confirmTTLDownloadOpen} file={selectedFileForDownload} onClose={() => setConfirmTTLDownloadOpen(false)} onConfirm={handleConfirmTTLDownload} />
        <DeleteConfirmDialog open={confirmDeleteOpen} file={selectedFileForDelete} onCancel={() => setConfirmDeleteOpen(false)} onConfirm={handleConfirmDelete} />
        <DeleteConfirmDialog open={confirmDeleteAllOpen} file={{ name: `all ${files.length} file(s)` }} onCancel={() => setConfirmDeleteAllOpen(false)} onConfirm={handleConfirmDeleteAll} />
        <SmartResultsDialog open={showSmartDialog} results={smartResults} onClose={() => setShowSmartDialog(false)} />
        <AnalysisDialog 
          open={analysisDialog} 
          analysis={selectedAnalysis} 
          onClose={() => setAnalysisDialog(false)} 
          onSmartAnalysisSuccess={() => {
            showSuccess('SMART analysis started successfully');
            setAnalysisDialog(false);
            navigate('/repository');
          }}
        />
        <TranslationConfigManager open={showConfigManager} onClose={() => setShowConfigManager(false)} onConfigCreated={loadTranslationConfigs} userPermissions={userPermissions} />
      </Box>
    </ThemeProvider>
  );
}

export default function AppWrapper() {
  return (
    <GlobalRestartProvider>
      <App />
    </GlobalRestartProvider>
  );
}